<template>
    <div
        :class="[
            'page-wrapper',
            'chiller-theme',
            'toggled',
            themeClass,
            { 'search-demo-shell': isSearchDemo },
        ]"
    >
        <SideBar
            ref="sidebar"
            :displayQuotebutton="sideBarProps.displayQuotebutton"
            :isBpaNoquote="sideBarProps.isBpaNoquote"
            @toggleQuoteModal="runSidebarHandler('toggleQuoteModal')"
        />

        <main class="page-content">
            <TopNavbar
                :enable-global-search="topNavbarProps.enableGlobalSearch"
                :shortcut-help-title="shortcutHelpModel.title"
                :shortcut-help-items="shortcutHelpModel.items"
                :theme-mode="themeMode"
                @toggleSidebar="toggleSidebar"
                @toggleTheme="toggleTheme"
                @resetSearch="runTopbarHandler('resetSearch')"
                @copyParts="runTopbarHandler('copyParts')"
                @showParts="runTopbarHandler('showParts')"
                @editParts="runTopbarHandler('editParts')"
                @editCustomerRef="runTopbarHandler('editCustomerRef')"
                @fuzzyPartsMatches="runTopbarHandler('fuzzyPartsMatches')"
                @downloadQuote="runTopbarHandler('downloadQuote')"
                @search="runTopbarHandler('search', $event)"
            />

            <router-view />
        </main>
    </div>
</template>

<script>
    import SideBar from '@/components/shared/SideBar.vue'
    import TopNavbar from '@/components/shared/TopNavbar.vue'
    import config from '@/services/config'
    import { getShortcutHelpForRoute } from '@/utils/shortcutRegistry'

    const storageThemeKey = 'crm-theme-mode'
    const defaultTopNavbarProps = {
        enableGlobalSearch: true,
    }

    const defaultSideBarProps = {
        displayQuotebutton: false,
        isBpaNoquote: false,
    }

    export default {
        name: 'AppShell',
        components: {
            SideBar,
            TopNavbar,
        },
        data() {
            return {
                topbarHandlers: {},
                sidebarHandlers: {},
                topNavbarProps: { ...defaultTopNavbarProps },
                sideBarProps: { ...defaultSideBarProps },
                themeMode: 'light',
            }
        },
        computed: {
            isSearchDemo() {
                return config.isSearchDemo()
            },
            shortcutHelpModel() {
                const shortcutHelp = getShortcutHelpForRoute(this.$route?.name)
                if (!config.isSearchDemo?.()) {
                    return shortcutHelp
                }

                const hiddenHandlers = {
                    dashboard: ['open_quote_modal'],
                    'my-quotes': ['download_quotes'],
                }
                const routeHiddenHandlers = hiddenHandlers[this.$route?.name] || []

                return {
                    ...shortcutHelp,
                    items: shortcutHelp.items.filter(
                        (item) => !routeHiddenHandlers.includes(item.handler)
                    ),
                }
            },
            themeClass() {
                return this.themeMode === 'dark' ? 'theme-dark' : 'theme-light'
            },
        },
        mounted() {
            const storedTheme =
                window.localStorage.getItem(storageThemeKey) ||
                document.documentElement.dataset.theme ||
                'light'
            this.setTheme(storedTheme)
        },
        provide() {
            return {
                appShell: {
                    setTopbarHandlers: this.setTopbarHandlers,
                    setSidebarHandlers: this.setSidebarHandlers,
                    setTopNavbarProps: this.setTopNavbarProps,
                    setSideBarProps: this.setSideBarProps,
                    resetLayout: this.resetLayout,
                },
            }
        },
        methods: {
            toggleSidebar() {
                this.$refs.sidebar?.toggleSidebar()
            },
            setTopbarHandlers(handlers = {}) {
                this.topbarHandlers = { ...handlers }
            },
            setSidebarHandlers(handlers = {}) {
                this.sidebarHandlers = { ...handlers }
            },
            setTopNavbarProps(props = {}) {
                this.topNavbarProps = {
                    ...defaultTopNavbarProps,
                    ...props,
                }
            },
            setSideBarProps(props = {}) {
                this.sideBarProps = {
                    ...defaultSideBarProps,
                    ...props,
                }
            },
            runTopbarHandler(handlerName, ...args) {
                const handler = this.topbarHandlers?.[handlerName]
                if (typeof handler === 'function') {
                    handler(...args)
                }
            },
            runSidebarHandler(handlerName, ...args) {
                const handler = this.sidebarHandlers?.[handlerName]
                if (typeof handler === 'function') {
                    handler(...args)
                }
            },
            setTheme(mode = 'light') {
                const nextMode = mode === 'dark' ? 'dark' : 'light'
                this.themeMode = nextMode
                document.documentElement.dataset.theme = nextMode
                document.documentElement.classList.toggle('theme-dark', nextMode === 'dark')
                document.documentElement.classList.toggle('theme-light', nextMode === 'light')
                window.localStorage.setItem(storageThemeKey, nextMode)
            },
            toggleTheme() {
                this.setTheme(this.themeMode === 'dark' ? 'light' : 'dark')
            },
            resetLayout() {
                this.topbarHandlers = {}
                this.sidebarHandlers = {}
                this.topNavbarProps = { ...defaultTopNavbarProps }
                this.sideBarProps = { ...defaultSideBarProps }
            },
        },
    }
</script>

<style>
    @media (max-width: 1023px) {
        .search-demo-shell .page-content {
            left: 72px;
            width: calc(100% - 72px) !important;
        }

        .search-demo-shell .page-content > div {
            padding-left: 12px;
            padding-right: 12px;
        }

        .search-demo-shell .top-navbar {
            gap: 8px;
            padding: 10px 12px;
        }

        .search-demo-shell .top-navbar-left,
        .search-demo-shell .top-navbar-right {
            gap: 8px;
        }

        .search-demo-shell .quick-actions-toggle,
        .search-demo-shell .theme-toggle-btn,
        .search-demo-shell .help-btn {
            min-width: 36px;
            min-height: 36px;
            justify-content: center;
            padding: 8px;
            margin: 0;
        }

        .search-demo-shell .quick-actions-toggle span,
        .search-demo-shell .theme-toggle-btn span,
        .search-demo-shell .help-btn span,
        .search-demo-shell .topbar-status {
            display: none;
        }

        .search-demo-shell .part-toolbar,
        .search-demo-shell .part-toolbar-main {
            flex-direction: column;
            gap: 12px;
        }

        .search-demo-shell .multi-part-selector {
            display: grid !important;
            grid-template-columns: 36px minmax(0, 1fr) 36px;
            gap: 8px;
            width: 100%;
        }

        .search-demo-shell .part-counter {
            grid-column: 1 / -1;
            height: auto !important;
        }

        .search-demo-shell .multi-part-selector select {
            min-width: 0;
            width: 100%;
            margin: 0 !important;
            font-size: 16px;
        }

        .search-demo-shell .part-toolbar-actions {
            flex-wrap: wrap;
            gap: 8px;
        }

        .search-demo-shell .part-toolbar-actions > div {
            flex: 1;
            margin: 0 !important;
        }

        .search-demo-shell .part-toolbar-actions button {
            width: 100%;
            font-size: 0.875rem;
        }

        .search-demo-shell .search-result-tables > div,
        .search-demo-shell .quote-page-body > div:last-child {
            max-width: 100%;
            overflow-x: auto;
        }

        .search-demo-shell .search-result-tables table + .d-flex {
            flex-wrap: wrap;
            gap: 8px;
        }

        .search-demo-shell .customer-summary-line {
            overflow-wrap: anywhere;
        }

        .search-demo-shell .search-tabs {
            margin-left: 0;
            margin-right: 0;
        }

        .search-demo-shell .search-tabs .tabs-wrapper {
            margin: 0;
            padding: 0;
        }

        .search-demo-shell .live-status-label {
            color: var(--color-text-secondary);
        }

        .search-demo-shell .quote-page-body {
            padding: 12px 0 !important;
        }

        .search-demo-shell .modal-content {
            margin: 0;
        }

        .search-demo-shell .modal-body,
        .search-demo-shell .shortcuts-modal__body {
            overflow-x: auto;
        }

        .search-demo-shell #showEditPartModal .modal-body > div:first-child {
            flex-direction: column;
            align-items: stretch !important;
        }
    }
</style>
